import { useState, useRef, useCallback } from 'react'
import { useTranscribeAudioMutation } from '@/services/api'

interface UseSpeechRecordingOptions {
  onTranscriptionReceived: (text: string) => void
  onError: (error: string) => void
  onTranscriptionFailed?: (audioBlob: Blob) => void
  enabled: boolean
}

interface UseSpeechRecordingReturn {
  isRecording: boolean
  isTranscribing: boolean
  startRecording: () => void
  stopRecording: () => void
  transcriptionError: string | null
  lastFailedAudioBlob: Blob | null
  retryLastTranscription: () => void
}

const MAX_RETRIES = 1

export const useSpeechRecording = (options: UseSpeechRecordingOptions): UseSpeechRecordingReturn => {
  const { onTranscriptionReceived, onError, onTranscriptionFailed, enabled } = options

  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)
  const [lastFailedAudioBlob, setLastFailedAudioBlob] = useState<Blob | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)

  const [transcribeAudio] = useTranscribeAudioMutation()

  const sendTranscriptionRequest = useCallback(async (audioBase64: string): Promise<string | null> => {
    const result = await transcribeAudio({
      audio_data: audioBase64,
      filename: 'speech_recording.webm'
    }).unwrap()

    if (result.transcription) {
      return result.transcription
    } else if (result.text) {
      return result.text
    }
    return null
  }, [transcribeAudio])

  const transcribeBlob = useCallback(async (audioBlob: Blob) => {
    setIsTranscribing(true)
    setTranscriptionError(null)

    try {
      const audioBuffer = await audioBlob.arrayBuffer()
      const audioArray = new Uint8Array(audioBuffer)
      const audioBase64 = btoa(String.fromCharCode(...audioArray))

      console.log('Sending audio for transcription, size:', audioBlob.size, 'bytes')

      let lastError: unknown = null

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          if (attempt > 0) {
            console.log(`Retrying transcription (attempt ${attempt + 1})...`)
          }

          const transcriptionText = await sendTranscriptionRequest(audioBase64)

          if (transcriptionText && transcriptionText.trim()) {
            console.log('Transcription received:', transcriptionText)
            setLastFailedAudioBlob(null)
            onTranscriptionReceived(transcriptionText.trim())
            return
          } else {
            console.warn('Empty transcription received')
            onError('No speech detected. Please try speaking more clearly.')
            return
          }
        } catch (error) {
          lastError = error
          console.error(`Transcription attempt ${attempt + 1} failed:`, error)
        }
      }

      let errorMessage = 'Speech transcription failed after retrying'
      if (lastError && typeof lastError === 'object' && 'data' in lastError) {
        errorMessage = `Transcription failed: ${(lastError as any).data?.detail || 'Unknown error'}`
      }

      setLastFailedAudioBlob(audioBlob)
      onTranscriptionFailed?.(audioBlob)
      onError(errorMessage)
      setTranscriptionError(errorMessage)
    } finally {
      setIsTranscribing(false)
      audioChunksRef.current = []
    }
  }, [sendTranscriptionRequest, onTranscriptionReceived, onError, onTranscriptionFailed])

  const startRecording = useCallback(async () => {
    if (isRecording) return

    try {
      setTranscriptionError(null)
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      })
      
      streamRef.current = stream
      audioChunksRef.current = []

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 16000
      })

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorderRef.current.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
          await transcribeBlob(audioBlob)
        }
      }

      mediaRecorderRef.current.start()
      setIsRecording(true)
      console.log('Started speech recording')

    } catch (error) {
      console.error('Failed to start speech recording:', error)
      onError('Failed to access microphone for speech recording')
    }
  }, [enabled, isRecording, onError, transcribeBlob])

  const stopRecording = useCallback(() => {
    if (!isRecording || !mediaRecorderRef.current) return

    console.log('Stopping speech recording')
    mediaRecorderRef.current.stop()
    setIsRecording(false)

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
  }, [isRecording])

  const retryLastTranscription = useCallback(async () => {
    if (!lastFailedAudioBlob) return
    await transcribeBlob(lastFailedAudioBlob)
  }, [lastFailedAudioBlob, transcribeBlob])

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    transcriptionError,
    lastFailedAudioBlob,
    retryLastTranscription
  }
}
